import { DataSource } from "@angular/cdk/collections";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { MatPaginator, MatSelectChange, MatSort } from "@angular/material";
import "rxjs/add/observable/fromEvent";
import "rxjs/add/observable/merge";
import "rxjs/add/operator/debounceTime";
import "rxjs/add/operator/distinctUntilChanged";
import "rxjs/add/operator/map";
import "rxjs/add/operator/startWith";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { fuseAnimations } from "../../../../core/animations";
import { FuseUtils } from "../../../../core/fuseUtils";
import { CategoriesService } from "../categories/categories.service";
import { ProductsService } from "./products.service";

@Component({
    selector: "fuse-products",
    templateUrl: "./products.component.html",
    styleUrls: ["./products.component.scss"],
    animations: fuseAnimations,
})
export class ProductsComponent implements OnInit {
    dataSource: FilesDataSource | null;
    displayedColumns = [
        "image",
        "title",
        "category",
        "quantity",
        "hidden" /* , 'quantityAvailable' */,
        "disabled",
    ];

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild("filter") filter: ElementRef;
    @ViewChild(MatSort) sort: MatSort;

    pageNumber = 0;
    pageLength = 0;

    constructor(
        private productsService: ProductsService,
        public readonly categoriesService: CategoriesService
    ) {}

    ngOnInit() {
        this.dataSource = new FilesDataSource(
            this.productsService,
            this.paginator,
            this.sort
        );
        Observable.fromEvent(this.filter.nativeElement, "keyup")
            .debounceTime(150)
            .distinctUntilChanged()
            .subscribe(() => {
                if (!this.dataSource) {
                    return;
                }
                this.dataSource.filter = this.filter.nativeElement.value;
            });
        this.categoriesService.getCategories();
        this.pageNumber = Number.parseInt(
            localStorage.getItem("productsPageNumber")
        );
        this.pageLength = Number.parseInt(
            localStorage.getItem("productsPageLength")
        );
        this.category = localStorage.getItem("productsCategory");

        this.type = localStorage.getItem("productsType");
    }

    storePaginatorState() {
        localStorage.setItem(
            "productsPageNumber",
            this.paginator.pageIndex.toString()
        );
        localStorage.setItem(
            "productsPageLength",
            this.paginator.pageSize.toString()
        );
    }

    set category(category: string) {
        setTimeout(() => {
            this.dataSource.category = category;
        }, 100);
        localStorage.setItem("productsCategory", category);
    }
    get category() {
        return localStorage.getItem("productsCategory");
    }

    set type(type: string) {
        setTimeout(() => {
            this.dataSource.type = type;
        }, 100);
        localStorage.setItem("productsType", type);
    }
    get type() {
        return localStorage.getItem("productsType");
    }

    selectChange(event: MatSelectChange) {
        this.paginator.pageIndex = 0;
        localStorage.setItem(
            "productsPageNumber",
            this.paginator.pageIndex.toString()
        );
    }
}

export class FilesDataSource extends DataSource<any> {
    _filterChange = new BehaviorSubject("");
    _filteredDataChange = new BehaviorSubject("");
    _categoryChange = new BehaviorSubject("");
    _typeChange = new BehaviorSubject("");

    get filteredData(): any {
        return this._filteredDataChange.value;
    }

    set filteredData(value: any) {
        this._filteredDataChange.next(value);
    }

    get filter(): string {
        return this._filterChange.value;
    }

    set filter(filter: string) {
        this._filterChange.next(filter);
    }

    get category(): string {
        return this._categoryChange.value;
    }

    set category(category: string) {
        this._categoryChange.next(category);
    }

    get type(): string {
        return this._typeChange.value;
    }

    set type(type: string) {
        this._typeChange.next(type);
    }

    constructor(
        private productsService: ProductsService,
        private _paginator: MatPaginator,
        private _sort: MatSort
    ) {
        super();
        this.filteredData = this.productsService.products;
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<any[]> {
        const displayDataChanges = [
            this.productsService.onProductsChanged,
            this._paginator.page,
            this._filterChange,
            this._sort.sortChange,
            this._categoryChange,
            this._typeChange,
        ];

        return Observable.merge(...displayDataChanges).map(() => {
            let data = this.productsService.products.slice();

            data = this.filterData(data);

            this.filteredData = [...data];

            data = this.sortData(data);

            // Grab the page's slice of data.
            const startIndex =
                this._paginator.pageIndex * this._paginator.pageSize;
            return data.splice(startIndex, this._paginator.pageSize);
        });
    }

    filterData(data) {
        let filteredData = data;
        if (this.filter)
            filteredData = FuseUtils.filterArrayByString(
                filteredData,
                this.filter
            );
        if (this.category)
            filteredData = this.filterCategory(filteredData, this.category);
        if (this.type) filteredData = this.filterType(filteredData, this.type);
        return filteredData;
    }

    filterCategory(data, categoryId) {
        return data.filter((product) => categoryId === product.category._id);
    }

    filterType(data, type) {
        const isHidden = type === "pro";
        return data.filter((product) => isHidden === product.hidden);
    }

    sortData(data): any[] {
        if (!this._sort.active || this._sort.direction === "") {
            return data;
        }

        return data.sort((a, b) => {
            let propertyA: number | string = "";
            let propertyB: number | string = "";

            switch (this._sort.active) {
                case "_id":
                    [propertyA, propertyB] = [a.id, b.id];
                    break;
                case "title":
                    [propertyA, propertyB] = [a.name, b.name];
                    break;
                // case 'parentCategory':
                //     [propertyA, propertyB] = [a.parentCategory.title, b.parentCategory.title];
                //     break;
                case "category":
                    [propertyA, propertyB] = [
                        a.category.title,
                        b.category.title,
                    ];
                    break;
                case "quantity":
                    [propertyA, propertyB] = [a.quantity, b.quantity];
                    break;
                case "disabled":
                    [propertyA, propertyB] = [a.active, b.active];
                    break;
            }

            const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
            const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

            return (
                (valueA < valueB ? -1 : 1) *
                (this._sort.direction === "asc" ? 1 : -1)
            );
        });
    }

    disconnect() {}
}



// WEBPACK FOOTER //
// /usr/app/src/app/main/content/app/products/products.component.ts